import type { Referencia } from "@base/contracts";
import { montarZipSemCompressao } from "./zip-store";

export type ArquivoParaDownload = {
  readonly url: string;
  readonly nomeArquivo: string;
};

type RefMidia = Pick<
  Referencia,
  "id" | "username" | "images" | "imageUrl" | "videoUrl" | "mediaType"
>;

/**
 * Lista URLs locais/remotas da referência para baixar (vídeo + imagens).
 */
export function listarArquivosDaReferencia(
  referencia: RefMidia,
): readonly ArquivoParaDownload[] {
  const base = nomeBaseDaReferencia(referencia);

  const saida: ArquivoParaDownload[] = [];
  const vistos = new Set<string>();

  const adicionar = (url: string | null | undefined, sufixo: string) => {
    if (!url || vistos.has(url)) return;
    vistos.add(url);
    saida.push({
      url,
      nomeArquivo: `${base}-${sufixo}${extensaoDaUrl(url)}`,
    });
  };

  if (referencia.videoUrl) {
    adicionar(referencia.videoUrl, "video");
  }

  const imagens =
    referencia.images.length > 0
      ? referencia.images
      : referencia.imageUrl
        ? [referencia.imageUrl]
        : [];

  imagens.forEach((url, indice) => {
    adicionar(url, imagens.length > 1 ? `img-${indice + 1}` : "img");
  });

  return saida;
}

export function nomeBaseDaReferencia(referencia: RefMidia): string {
  return [
    referencia.username?.replace(/^@/, "") || "referencia",
    referencia.id,
  ]
    .join("-")
    .replace(/[^\w.-]+/g, "_");
}

function extensaoDaUrl(url: string): string {
  try {
    const caminho = new URL(url, "http://local").pathname;
    const match = caminho.match(/\.(jpe?g|png|webp|gif|mp4|mov|webm)(?:$|\?)/i);
    if (match?.[1]) return `.${match[1].toLowerCase()}`;
  } catch {
    /* ignore */
  }
  if (url.includes("/video") || url.endsWith(".mp4")) return ".mp4";
  return ".bin";
}

async function buscarBlob(
  arquivo: ArquivoParaDownload,
  token: string | null,
): Promise<Blob> {
  const headers: HeadersInit = {};
  if (
    token &&
    arquivo.url.startsWith("/api/") &&
    !arquivo.url.includes("sig=")
  ) {
    headers.authorization = `Bearer ${token}`;
  }

  const resposta = await fetch(arquivo.url, { headers });
  if (!resposta.ok) {
    throw new Error(`Falha ao baixar (${resposta.status})`);
  }
  return resposta.blob();
}

function dispararDownload(blob: Blob, nomeArquivo: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = nomeArquivo;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Baixa um arquivo (Bearer se for `/api/...` sem assinatura).
 */
export async function baixarArquivo(
  arquivo: ArquivoParaDownload,
  token: string | null,
): Promise<void> {
  const blob = await buscarBlob(arquivo, token);
  dispararDownload(blob, arquivo.nomeArquivo);
}

/**
 * 1 mídia → download direto. 2+ → um .zip com tudo.
 */
export async function baixarMidiasDaReferencia(
  referencia: RefMidia,
  token: string | null,
): Promise<number> {
  const arquivos = listarArquivosDaReferencia(referencia);
  if (arquivos.length === 0) return 0;

  if (arquivos.length === 1) {
    await baixarArquivo(arquivos[0]!, token);
    return 1;
  }

  const entradas = [];
  for (const arquivo of arquivos) {
    const blob = await buscarBlob(arquivo, token);
    entradas.push({
      nome: arquivo.nomeArquivo,
      dados: new Uint8Array(await blob.arrayBuffer()),
    });
  }

  const zipBytes = montarZipSemCompressao(entradas);
  const buffer = new ArrayBuffer(zipBytes.byteLength);
  new Uint8Array(buffer).set(zipBytes);
  dispararDownload(
    new Blob([buffer], { type: "application/zip" }),
    `${nomeBaseDaReferencia(referencia)}.zip`,
  );
  return arquivos.length;
}
